import React, { useState, useEffect } from 'react';
import { formatDateOnly } from '../../utils/dates';
import { API_BASE } from '../../config';
import { printShoppingList, printRecipeCards, printRecipeBook, buildRecipeBookHTML, printHTMLDocument } from '../../utils/printHelper';
import PreferenceForm from './PreferenceForm';
import RecipeForm from './RecipeForm';
import ShoppingListForm from './ShoppingListForm';
import UhcCompatBadge from '../UhcCompatBadge';
import { CitationChip } from '../Citations';
import PantryDetailView from '../PantryDetailView';
import DietPlanManagement from '../DietPlanManagement';
import MealPlanManagement from '../MealPlanManagement';
import ListControls from '../ListControls';
import Pagination from '../Pagination';
import FamilyAllergenGuard from '../FamilyAllergenGuard';
import CarbonFootprintDashboard from '../CarbonFootprintDashboard';
import NutritionComparisonMode from '../NutritionComparisonMode';
import VisionGroceryCapture from '../VisionGroceryCapture';
import DietComplianceChecker from '../DietComplianceChecker';
import BarcodeLookupModal from '../BarcodeLookupModal';
import MealogicHandoff from '../MealogicHandoff';
import { authedBlobUrl, revokeBlobUrl, downloadAuthedFile } from '../../utils/authedBlobUrl';
import { useConfirm } from '../ConfirmProvider';

function NutritionTab({ fetchWithAuth, fetchWithAuthFormData, setShowAIAssistant, setAiAgentInitialTool, refreshKey, token, setActiveTab, setShowWhatCanICook }) {
  const confirm = useConfirm();
  const [nutritionSubTab, setNutritionSubTab] = useState('preferences');
  const [preferences, setPreferences] = useState([]);
  const [pantry, setPantry] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [showPreferenceForm, setShowPreferenceForm] = useState(false);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [selectedPantry, setSelectedPantry] = useState(null);
  const [editingPreference, setEditingPreference] = useState(null);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRecipeViewer, setShowRecipeViewer] = useState(false);
  const [selectedRecipeForView, setSelectedRecipeForView] = useState(null);
  // H7: blob: object URL for the recipe viewer iframe — see openRecipeViewer below.
  const [recipeViewUrl, setRecipeViewUrl] = useState(null);
  // shoppingLists state removed — single cart model, no list history
  const [showShoppingListForm, setShowShoppingListForm] = useState(false);
  const [editingShoppingList, setEditingShoppingList] = useState(null);
  const [showShoppingListViewer, setShowShoppingListViewer] = useState(false);
  const [selectedShoppingList, setSelectedShoppingList] = useState(null);
  // Mealogic pre-prepared meals — the OTHER fulfilment path. Instacart buys the ingredients;
  // Mealogic ships the finished meal. Both live on the shopping list because that is where the
  // user decides HOW to get the food, which is the decision Mealogic actually answers.
  //
  // Deliberately NOT on a recipe card, although the first mockup put it there: Mealogic cannot
  // supply "this recipe, pre-made". It supplies its own menu, filtered to the user's health
  // profile. A button on a recipe card would promise a dish they cannot order.
  const [showMealogic, setShowMealogic] = useState(false);

  // Instacart Order Review — 3-stage modal: 1=review, 2=redirected, 3=confirm
  const [instacartReview, setInstacartReview] = useState({
    show: false, stage: 1, list: null, url: null,
    selectedIndices: [], addItemForm: { name: '', quantity: '', unit: '' },
    showAddItem: false, showBarcodeLookup: false
  });
  const [showCartScanModal, setShowCartScanModal] = useState(false);
  const [scanCartId, setScanCartId] = useState(null);
  const [showFulfillModal, setShowFulfillModal] = useState(false);
  const [fulfillSelectedItems, setFulfillSelectedItems] = useState([]);
  const [isFulfilling, setIsFulfilling] = useState(false);
  const [showFamilyGuard, setShowFamilyGuard] = useState(false);
  // Active shopping list state
  const [activeShoppingList, setActiveShoppingList] = useState(null);
  const [showCartViewModal, setShowCartViewModal] = useState(false);
  // Fulfilled cart prompt state — shown when add-to-cart hits a fulfilled cart
  const [showFulfilledCartPrompt, setShowFulfilledCartPrompt] = useState(false);
  const [fulfilledCartRetryAction, setFulfilledCartRetryAction] = useState(null);
  const [showCarbonDashboard, setShowCarbonDashboard] = useState(false);
  const [showNutritionComparison, setShowNutritionComparison] = useState(false);
  const [showGroceryCapture, setShowGroceryCapture] = useState(false);
  const [showDietCheck, setShowDietCheck] = useState(false);
  // Recipe pagination state
  const [recipeSearch, setRecipeSearch] = useState('');
  const [recipeSort, setRecipeSort] = useState('newest');
  const [recipePerPage, setRecipePerPage] = useState(25);
  const [recipePage, setRecipePage] = useState(1);
  const [recipesTotal, setRecipesTotal] = useState(0);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [showAddToCartModal, setShowAddToCartModal] = useState(false);
  const [addToCartRecipeId, setAddToCartRecipeId] = useState(null);
  const [addToCartRecipeName, setAddToCartRecipeName] = useState('');
  const [addToCartMode, setAddToCartMode] = useState('new'); // 'new' or 'existing'
  const [addToCartExistingListId, setAddToCartExistingListId] = useState('');
  const [addToCartServings, setAddToCartServings] = useState(1);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [slvDrugInteractions, setSlvDrugInteractions] = useState({});
  // A failed food–drug check is SHOWN (2026-11-02): no badges is what a clean list looks like.
  const [slvDrugCheckFailed, setSlvDrugCheckFailed] = useState(false);
  const [openDrugBadge, setOpenDrugBadge] = useState(null);   // which shopping-list drug badge is expanded
  // NCM scanner state removed — scanner is now self-contained in NutritionComparisonMode.jsx

  // Recipe Cards / Recipe Book state
  const [selectedRecipes, setSelectedRecipes] = useState(new Set());
  const [recipeBookFilter, setRecipeBookFilter] = useState('all'); // 'all' | 'favorites'
  const [openOverflowId, setOpenOverflowId] = useState(null);
  const [showPostFulfillPrint, setShowPostFulfillPrint] = useState(false);
  const [postFulfillRecipeIds, setPostFulfillRecipeIds] = useState([]);
  const [recipeBookGenerating, setRecipeBookGenerating] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  // Recipe Book Management
  const [showRecipeBookModal, setShowRecipeBookModal] = useState(false);
  const [recipeBooks, setRecipeBooks] = useState([]);
  const [recipeBooksLoading, setRecipeBooksLoading] = useState(false);
  const [viewingBookUrl, setViewingBookUrl] = useState(null);

  // Instacart Provided Official Carrot Logo
  const InstacartCarrotLogo = ({ size = 22 }) => (
    <img
      src="/assets/instacart-carrot.svg"
      alt="Instacart"
      style={{ width: size, height: 'auto' }}
    />
  );

  useEffect(() => {
    loadNutritionData();
  }, [refreshKey]);

  // Load recipes separately with pagination
  useEffect(() => {
    loadRecipes();
  }, [refreshKey, recipeSearch, recipeSort, recipePerPage, recipePage, recipeBookFilter]);

  const loadRecipes = async () => {
    setRecipesLoading(true);
    try {
      const params = new URLSearchParams();
      if (recipeSearch) params.set('search', recipeSearch);
      params.set('sort', recipeSort);
      if (recipeBookFilter === 'favorites') params.set('is_favorite', 'true');
      if (recipePerPage > 0) {
        params.set('limit', recipePerPage);
        params.set('offset', (recipePage - 1) * recipePerPage);
      } else {
        params.set('limit', 200);
        params.set('offset', 0);
      }
      const [res, favRes] = await Promise.all([
        fetchWithAuth(`${API_BASE}/api/recipes/?${params}`),
        fetchWithAuth(`${API_BASE}/api/recipes/?is_favorite=true&limit=1&offset=0`)
      ]);
      if (res.ok) {
        const data = await res.json();
        setRecipes(data.recipes || []);
        setRecipesTotal(data.total || 0);
      }
      if (favRes.ok) {
        const favData = await favRes.json();
        setFavoritesCount(favData.total || 0);
      }
    } catch (error) {
      console.error('Failed to load recipes:', error);
    } finally {
      setRecipesLoading(false);
    }
  };

  const loadNutritionData = async () => {
    try {
      const [preferencesRes, pantryRes] = await Promise.all([
        fetchWithAuth(`${API_BASE}/api/health/preferences`),
        fetchWithAuth(`${API_BASE}/api/pantry/`)
      ]);

      const preferences = await preferencesRes.json();
      const pantryData = await pantryRes.json();

      setPreferences(preferences);
      setPantry(pantryData.pantry || pantryData);
    } catch (error) {
      console.error('Failed to load nutrition data:', error);
    }
  };

  // Load active shopping list
  const loadActiveList = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/shopping-lists/active`);
      if (res.ok) {
        const data = await res.json();
        setActiveShoppingList(data);
      }
    } catch (error) {
      console.error('Failed to load active shopping list:', error);
    }
  };

  useEffect(() => {
    loadActiveList();
  }, [refreshKey]);

  // Fulfilled cart prompt — replace fulfilled cart + retry original action
  const handleReplaceFulfilledCart = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/shopping-lists/replace-fulfilled`, {
        method: 'POST'
      });
      if (res.ok) {
        await loadActiveList();
        setShowFulfilledCartPrompt(false);
        // Retry the original action
        if (fulfilledCartRetryAction) {
          fulfilledCartRetryAction();
          setFulfilledCartRetryAction(null);
        }
      } else {
        alert('Failed to create new shopping list');
      }
    } catch (err) {
      console.error('Failed to replace fulfilled cart:', err);
      alert('Error creating new shopping list');
    }
  };

  // Check if a 409 CART_FULFILLED response — show prompt instead of error
  const handleCartFulfilledError = (retryAction) => {
    setFulfilledCartRetryAction(() => retryAction);
    setShowFulfilledCartPrompt(true);
  };

  // Food Preferences handlers (keep existing)
  const handleEditPreference = (preference) => {
    setEditingPreference(preference);
    setShowPreferenceForm(true);
  };

  const handleDeletePreference = async (preferenceId) => {
    if (await confirm({
      title: 'Delete this food preference?',
      message: 'Healist will stop factoring it into meal plans, recipe suggestions and diet checks.',
      confirmLabel: 'Delete Preference',
      destructive: true,
    })) {
      try {
        const response = await fetchWithAuth(`${API_BASE}/api/health/preferences/${preferenceId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadNutritionData();
        }
      } catch (error) {
        console.error('Failed to delete preference:', error);
      }
    }
  };

  const closePreferenceForm = () => {
    setShowPreferenceForm(false);
    setEditingPreference(null);
  };

  // Recipe handlers
  const handleEditRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setShowRecipeForm(true);
  };

  const handleDeleteRecipe = async (recipeId) => {
    if (await confirm({
      title: 'Delete this recipe?',
      message: 'It will be removed from your recipes and from any recipe book it belongs to. This cannot be undone.',
      confirmLabel: 'Delete Recipe',
      destructive: true,
    })) {
      try {
        const response = await fetchWithAuth(`${API_BASE}/api/recipes/${recipeId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadNutritionData();
        }
      } catch (error) {
        console.error('Failed to delete recipe:', error);
      }
    }
  };

  const closeRecipeForm = () => {
    setShowRecipeForm(false);
    setEditingRecipe(null);
  };

  // Shopping List handlers
  const handleEditShoppingList = (list) => {
    setEditingShoppingList(list);
    setShowShoppingListForm(true);
  };

  const handleDeleteShoppingList = async (listId) => {
    if (await confirm({
      title: 'Delete this shopping list?',
      message: 'The list and everything on it will be permanently removed. This cannot be undone.',
      confirmLabel: 'Delete List',
      destructive: true,
    })) {
      try {
        const response = await fetchWithAuth(`${API_BASE}/api/shopping-lists/${listId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadNutritionData();
        }
      } catch (error) {
        console.error('Failed to delete shopping list:', error);
      }
    }
  };

  const closeShoppingListForm = () => {
    setShowShoppingListForm(false);
    setEditingShoppingList(null);
  };

  const handleOpenCartScan = (cartId) => {
    // Close the form first, then open scan modal
    setShowShoppingListForm(false);
    setEditingShoppingList(null);
    setScanCartId(cartId);
    setShowCartScanModal(true);
  };

  const handleViewShoppingList = async (list) => {
    setSelectedShoppingList(list);
    setShowShoppingListViewer(true);
    setSlvDrugInteractions({});
    setSlvDrugCheckFailed(false);
    // Auto-check food-drug interactions for SLV items
    try {
      const foodNames = (list.items || []).map(i => i.name || i.item_name).filter(Boolean);
      if (foodNames.length > 0) {
        const res = await fetchWithAuth(`${API_BASE}/api/food-drug/check-batch`, {
          method: 'POST',
          body: JSON.stringify({ food_names: foodNames })
        });
        if (res.ok) {
          const data = await res.json();
          setSlvDrugInteractions(data.results || {});
        } else {
          setSlvDrugCheckFailed(true);
        }
      }
    } catch (e) {
      // Non-blocking — the list still opens — but the user is told the check did not run.
      console.warn('Shopping list food-drug check failed', e);
      setSlvDrugCheckFailed(true);
    }
  };

  // Shopping List Fulfillment Handler
  const handleFulfillShoppingList = async () => {
    if (!selectedShoppingList) {
      alert('Please select a shopping list');
      return;
    }

    // Get indices of selected items (or all if none specifically selected)
    const itemIndices = fulfillSelectedItems.length > 0
      ? fulfillSelectedItems
      : selectedShoppingList.items?.map((_, idx) => idx) || [];

    if (itemIndices.length === 0) {
      alert('No items to fulfill');
      return;
    }

    setIsFulfilling(true);

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/shopping-lists/${selectedShoppingList.id}/fulfill`, {
        method: 'POST',
        body: JSON.stringify({
          item_indices: itemIndices,
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`${result.fulfilled_count} items added to ${result.destination_pantry}!`);

        // Check for source recipe IDs → offer print prompt
        const items = selectedShoppingList.items || [];
        const fulfilledItems = (fulfillSelectedItems.length > 0
          ? fulfillSelectedItems.map(i => items[i])
          : items
        ).filter(Boolean);
        const recipeIds = [...new Set(fulfilledItems.map(i => i.source_recipe_id).filter(Boolean))];
        if (recipeIds.length > 0) {
          setPostFulfillRecipeIds(recipeIds);
          setShowPostFulfillPrint(true);
        }

        // Refresh data
        loadNutritionData();

        // Close modals
        setShowFulfillModal(false);
        setShowShoppingListViewer(false);
        setSelectedShoppingList(null);
        setFulfillSelectedItems([]);
      } else {
        const error = await response.json();
        alert(`Failed to fulfill: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Fulfill error:', error);
      alert('Failed to fulfill shopping list. Please try again.');
    } finally {
      setIsFulfilling(false);
    }
  };

  // Instacart Order Review — open Stage 1
  const handleSendToInstacart = (list) => {
    if (!list || !list.items || list.items.length === 0) {
      alert('Shopping list has no items to send');
      return;
    }
    // If already has an Instacart link (user returning from shopping), go to Stage 3
    const savedIndices = list.verification_results?.instacart_selected_indices;
    if (list.instacart_link_url && list.status === 'pending' && savedIndices) {
      setInstacartReview({
        show: true, stage: 3, list: list, url: list.instacart_link_url,
        selectedIndices: savedIndices, addItemForm: { name: '', quantity: '', unit: '' },
        showAddItem: false, showBarcodeLookup: false
      });
      return;
    }
    // Default: Stage 1 — Order Review with all items pre-selected
    const allIndices = list.items.map((_, i) => i);
    setInstacartReview({
      show: true, stage: 1, list: { ...list, items: [...list.items] }, url: null,
      selectedIndices: allIndices, addItemForm: { name: '', quantity: '', unit: '' },
      showAddItem: false, showBarcodeLookup: false
    });
  };

  // Instacart Review — toggle item selection
  const handleIcrToggleItem = (idx) => {
    setInstacartReview(prev => {
      const sel = prev.selectedIndices.includes(idx)
        ? prev.selectedIndices.filter(i => i !== idx)
        : [...prev.selectedIndices, idx];
      return { ...prev, selectedIndices: sel };
    });
  };

  // Instacart Review — select/deselect all
  const handleIcrSelectAll = () => {
    setInstacartReview(prev => {
      const allSelected = prev.selectedIndices.length === (prev.list?.items?.length || 0);
      return {
        ...prev,
        selectedIndices: allSelected ? [] : (prev.list?.items || []).map((_, i) => i)
      };
    });
  };

  // Instacart Review — add manual item
  const handleIcrAddManualItem = () => {
    const { name, quantity, unit } = instacartReview.addItemForm;
    if (!name.trim()) return;
    setInstacartReview(prev => {
      const items = [...(prev.list?.items || [])];
      const newIdx = items.length;
      items.push({
        name: name.trim(),
        quantity: parseFloat(quantity) || 1,
        unit: unit.trim() || 'each',
        fulfilled: false, added_via: 'instacart_review'
      });
      return {
        ...prev,
        list: { ...prev.list, items },
        selectedIndices: [...prev.selectedIndices, newIdx],
        addItemForm: { name: '', quantity: '', unit: '' },
        showAddItem: false
      };
    });
  };

  // Instacart Review — add item from barcode lookup
  const handleIcrBarcodeProduct = (barcode, productData) => {
    setInstacartReview(prev => {
      const items = [...(prev.list?.items || [])];
      const newIdx = items.length;
      items.push({
        name: productData.name || productData.product_name || 'Unknown Product',
        quantity: 1,
        unit: 'each',
        upc: barcode,
        brand: productData.brand || '',
        fulfilled: false, added_via: 'barcode_scan'
      });
      return {
        ...prev,
        list: { ...prev.list, items },
        selectedIndices: [...prev.selectedIndices, newIdx],
        showBarcodeLookup: false
      };
    });
  };

  // Instacart Review — Stage 1 → send selected items to Instacart
  const handleIcrSendToInstacart = async () => {
    const { list, selectedIndices } = instacartReview;
    if (!list || selectedIndices.length === 0) return;

    // First, save any newly added items to the shopping list
    const origItemCount = activeShoppingList?.items?.length || 0;
    if (list.items.length > origItemCount) {
      try {
        await fetchWithAuth(`${API_BASE}/api/shopping-lists/${list.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: list.items })
        });
      } catch (err) {
        console.error('Failed to save new items:', err);
        alert('Failed to save added items');
        return;
      }
    }

    // Open window IMMEDIATELY (sync with click) to avoid Safari popup blocker
    const newWindow = window.open('about:blank', '_blank');

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/instacart/shopping-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopping_list_id: list.id,
          expires_in_days: 7,
          selected_indices: selectedIndices
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create Instacart list');
      }

      const data = await response.json();

      if (data.success && data.url) {
        if (newWindow) newWindow.location.href = data.url;
        setInstacartReview(prev => ({ ...prev, stage: 2, url: data.url }));
        loadActiveList();
      } else {
        if (newWindow) newWindow.close();
        throw new Error(data.error || 'Failed to create Instacart link');
      }
    } catch (err) {
      if (newWindow) newWindow.close();
      console.error('Instacart error:', err);
      alert(`Failed to send to Instacart: ${err.message}`);
    }
  };

  // Instacart Review — Stage 3: Mark Fulfilled (transfer to pantry)
  const handleIcrMarkFulfilled = async () => {
    const { list, selectedIndices } = instacartReview;
    if (!list) return;
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/shopping-lists/${list.id}/fulfill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_indices: selectedIndices })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fulfill items');
      }

      // Check for source recipe IDs on fulfilled items → offer print prompt
      const items = list.items || [];
      const fulfilledItems = selectedIndices.map(i => items[i]).filter(Boolean);
      const recipeIds = [...new Set(fulfilledItems.map(i => i.source_recipe_id).filter(Boolean))];
      if (recipeIds.length > 0) {
        setPostFulfillRecipeIds(recipeIds);
        setShowPostFulfillPrint(true);
      }

      setInstacartReview({ show: false, stage: 1, list: null, url: null, selectedIndices: [], addItemForm: { name: '', quantity: '', unit: '' }, showAddItem: false, showBarcodeLookup: false });
      loadActiveList();
    } catch (err) {
      console.error('Fulfill error:', err);
      alert(`Failed to mark as fulfilled: ${err.message}`);
    }
  };

  // Instacart Review — Cancel order (clear Instacart link, keep pending)
  const handleIcrCancelOrder = async () => {
    const { list } = instacartReview;
    if (!list) return;
    try {
      // Clear Instacart link data but keep list pending
      await fetchWithAuth(`${API_BASE}/api/shopping-lists/${list.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'pending' })
      });
      setInstacartReview({ show: false, stage: 1, list: null, url: null, selectedIndices: [], addItemForm: { name: '', quantity: '', unit: '' }, showAddItem: false, showBarcodeLookup: false });
      loadActiveList();
    } catch (err) {
      console.error('Cancel order error:', err);
    }
  };

  // Instacart Review — close modal
  const handleIcrClose = () => {
    setInstacartReview({ show: false, stage: 1, list: null, url: null, selectedIndices: [], addItemForm: { name: '', quantity: '', unit: '' }, showAddItem: false, showBarcodeLookup: false });
  };

  // Print Shopping List - iframe print (no status change — two-state model)
  const handlePrintShoppingList = async (list) => {
    if (!list || !list.items || list.items.length === 0) {
      alert('Shopping list has no items to print');
      return;
    }
    printShoppingList(list);
  };

  // Toggle item selection for partial fulfillment
  const toggleFulfillItem = (index) => {
    setFulfillSelectedItems(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  // getStatusBadge removed — single cart model, no multi-status display needed

  // ── H7 (2026-08-04): recipe viewer over an authenticated fetch + blob: URL ────────────────
  // The iframe src used to be `/api/recipes/{id}/view?token=${localStorage.token}` — a live session
  // credential in a URL, which lands in access logs, browser history and Referer and keeps working
  // wherever it leaks until the token expires. The HTML is now fetched with an Authorization header
  // and given to the iframe as an in-memory blob, so there is no URL to leak. The backend renders
  // self-contained HTML (image inlined as a data: URI) because a blob document's origin is opaque
  // and cannot resolve root-relative URLs.
  // A PDF attachment CANNOT be embedded inside the generated HTML — Chrome blocks data: URLs for
  // frame navigation, so there is no self-contained way to show it from within that document. It
  // renders perfectly as a blob: URL in the parent's own iframe though, so the viewer fetches the
  // PDF itself rather than showing a card that tells the user their file is unavailable. Same
  // shape as MealPlanManagement's __PDF__ path.
  const openRecipeViewer = async (recipe) => {
    revokeBlobUrl(recipeViewUrl);
    setRecipeViewUrl(null);
    setSelectedRecipeForView(recipe);
    setShowRecipeViewer(true);
    const isPdf = recipe.mime_type === 'application/pdf' && !!recipe.file_path;
    const url = isPdf
      ? `${API_BASE}/api/recipes/${recipe.id}/download`
      : `${API_BASE}/api/recipes/${recipe.id}/view`;
    try {
      setRecipeViewUrl(await authedBlobUrl(fetchWithAuth, url));
    } catch (err) {
      console.error('Failed to load recipe view:', err);
    }
  };

  // Recipes can carry an attachment of any allowed type. Before 2026-08-04 there was no way to get
  // it back out of the app at all: the viewer's "Download File" links pointed at
  // /api/recipes/{id}/file, which does not exist, and nothing called the real endpoint — which was
  // itself broken (it served an S3 key as a local path). Both are fixed; this is the control that
  // makes the file reachable.
  const handleDownloadRecipeFile = async (recipe) => {
    const name = (recipe.file_path || '').split('/').pop() || `${recipe.title || 'recipe'}`;
    try {
      await downloadAuthedFile(fetchWithAuth, `${API_BASE}/api/recipes/${recipe.id}/download`, name);
    } catch (err) {
      console.error('Failed to download recipe file:', err);
      alert('Failed to download the attached file.');
    }
  };

  // Every close path routes through here — an un-revoked blob URL pins its bytes for the lifetime
  // of the document.
  const closeRecipeViewer = () => {
    revokeBlobUrl(recipeViewUrl);
    setRecipeViewUrl(null);
    setShowRecipeViewer(false);
    setSelectedRecipeForView(null);
  };

  // Shared Make Recipe handler — used by recipe cards ("Cook This") and RecipeViewer ("Make Recipe")
  const handleMakeRecipe = async (recipeId, recipeName) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/api/pantry/user/make-recipe/${recipeId}`,
        { method: 'POST' }
      );
      if (response.status === 401) {
        alert('Session expired. Please refresh the page and try again.');
        return;
      }
      if (response.ok) {
        const result = await response.json();
        const lines = [`Recipe: ${result.recipe_name || recipeName || 'Done'}\n`];
        if (result.items_decremented?.length > 0) {
          lines.push('Used from pantry:');
          result.items_decremented.forEach(i => lines.push(`  ${i.name}: ${i.previous_qty} → ${i.new_qty}`));
        }
        if (result.items_depleted?.length > 0) {
          lines.push('\nUsed up completely:');
          result.items_depleted.forEach(i => lines.push(`  ${i.name} (was ${i.previous_qty})`));
        }
        if (result.items_missing?.length > 0) {
          lines.push('\nNot in pantry:');
          result.items_missing.forEach(i => lines.push(`  ${i.name} (${i.quantity} ${i.unit})`));
        }
        if (result.items_opened?.length > 0) {
          lines.push('\nOpened (new expiry):');
          result.items_opened.forEach(i => lines.push(`  ${i.name} — use by ${i.new_expiry}`));
        }
        if (result.items_thawed?.length > 0) {
          lines.push('\nAuto-thawed:');
          result.items_thawed.forEach(i => lines.push(`  ${i.name} — use within ${i.days_to_use} days`));
        }
        alert(lines.join('\n'));
      } else {
        const errData = await response.json().catch(() => ({}));
        const detail = errData.detail || 'Failed to update pantry inventory';
        alert(typeof detail === 'string' ? detail : detail.map(d => d.msg || d).join(', '));
      }
    } catch (error) {
      console.error('Error making recipe:', error);
      alert('Error updating pantry inventory: ' + error.message);
    }
  };

  // === Recipe Cards / Recipe Book handlers ===

  const handleToggleFavorite = async (recipeId) => {
    const recipe = recipes.find(r => r.id === recipeId);
    if (!recipe) return;
    const newVal = !recipe.is_favorite;
    // Optimistic UI
    setRecipes(prev => prev.map(r => r.id === recipeId ? { ...r, is_favorite: newVal } : r));
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/recipes/${recipeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_favorite: newVal })
      });
      if (res.ok) {
        setFavoritesCount(prev => newVal ? prev + 1 : Math.max(0, prev - 1));
      } else {
        // Revert on failure
        setRecipes(prev => prev.map(r => r.id === recipeId ? { ...r, is_favorite: !newVal } : r));
      }
    } catch {
      setRecipes(prev => prev.map(r => r.id === recipeId ? { ...r, is_favorite: !newVal } : r));
    }
  };

  const handleSelectRecipe = (recipeId) => {
    setSelectedRecipes(prev => {
      const next = new Set(prev);
      if (next.has(recipeId)) next.delete(recipeId);
      else next.add(recipeId);
      return next;
    });
  };

  const handleSelectAllRecipes = () => {
    if (selectedRecipes.size === recipes.length) {
      setSelectedRecipes(new Set());
    } else {
      setSelectedRecipes(new Set(recipes.map(r => r.id)));
    }
  };

  const handleDeselectAll = () => setSelectedRecipes(new Set());

  const handleBulkDelete = async () => {
    if (selectedRecipes.size === 0) return;
    if (!(await confirm({
      title: 'Delete selected recipes?',
      message: `${selectedRecipes.size} recipe${selectedRecipes.size === 1 ? '' : 's'} will be permanently deleted. This cannot be undone.`,
      confirmLabel: `Delete ${selectedRecipes.size} Recipe${selectedRecipes.size === 1 ? '' : 's'}`,
      destructive: true,
    }))) return;
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/recipes/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipe_ids: [...selectedRecipes] })
      });
      if (res.ok) {
        setSelectedRecipes(new Set());
        loadRecipes();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to delete recipes');
      }
    } catch (e) {
      alert('Failed to delete recipes: ' + e.message);
    }
  };

  const handleBulkFavorite = async () => {
    if (selectedRecipes.size === 0) return;
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/recipes/bulk-favorite`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipe_ids: [...selectedRecipes], is_favorite: true })
      });
      if (res.ok) {
        setSelectedRecipes(new Set());
        loadRecipes();
      }
    } catch (e) {
      alert('Failed to update favorites: ' + e.message);
    }
  };

  const handlePrintSelected = () => {
    const selected = recipes.filter(r => selectedRecipes.has(r.id));
    if (selected.length === 0) return;
    printRecipeCards(selected);
  };

  const handlePrintSingleCard = (recipe) => {
    printRecipeCards([recipe]);
  };

  const loadRecipeBooks = async () => {
    setRecipeBooksLoading(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/recipes/books`);
      if (res.ok) {
        const data = await res.json();
        setRecipeBooks(data);
      }
    } catch { /* ignore */ }
    setRecipeBooksLoading(false);
  };

  const handleViewRecipeBook = async (bookId) => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/recipes/books/${bookId}/view`);
      if (res.ok) {
        const data = await res.json();
        setViewingBookUrl(data.view_url);
      }
    } catch (e) {
      alert('Failed to load recipe book: ' + e.message);
    }
  };

  const handlePrintRecipeBook = async (bookId) => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/recipes/books/${bookId}/view`);
      if (res.ok) {
        const data = await res.json();
        const htmlRes = await fetch(data.view_url);
        if (htmlRes.ok) {
          const htmlContent = await htmlRes.text();
          printHTMLDocument(htmlContent, 'Recipe Book');
        }
      }
    } catch (e) {
      alert('Failed to print recipe book: ' + e.message);
    }
  };

  const handleDeleteRecipeBook = async (bookId) => {
    if (!(await confirm({
      title: 'Delete this recipe book?',
      message: 'The book will be deleted. The recipes inside it stay in your collection.',
      confirmLabel: 'Delete Book',
      destructive: true,
    }))) return;
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/recipes/books/${bookId}`, { method: 'DELETE' });
      if (res.ok) {
        setRecipeBooks(prev => prev.filter(b => b.id !== bookId));
      }
    } catch (e) {
      alert('Failed to delete: ' + e.message);
    }
  };

  const handleGenerateRecipeBook = async () => {
    if (favoritesCount === 0) {
      alert('Add recipes to your Recipe Book first by clicking the \u2665 heart icon.');
      return;
    }
    setRecipeBookGenerating(true);
    try {
      // Fetch all favorites
      const favRes = await fetchWithAuth(`${API_BASE}/api/recipes/?is_favorite=true&limit=200&offset=0`);
      if (!favRes.ok) throw new Error('Failed to load favorites');
      const favData = await favRes.json();
      const favRecipes = favData.recipes || [];
      if (favRecipes.length === 0) {
        alert('No recipes in your Recipe Book.');
        return;
      }

      // Ensure ALL recipes have images before building book
      const recipesWithImages = await Promise.all(
        favRecipes.map(async (recipe) => {
          if (recipe.image_url) return recipe;
          try {
            const imgRes = await fetchWithAuth(`${API_BASE}/api/recipes/${recipe.id}/generate-image`, { method: 'POST' });
            if (imgRes.ok) {
              const imgData = await imgRes.json();
              return { ...recipe, image_url: imgData.image_url };
            }
          } catch { /* proceed without image if generation fails */ }
          return recipe;
        })
      );

      // Get or generate cover
      let coverUrl = null;
      try {
        const coverRes = await fetchWithAuth(`${API_BASE}/api/recipes/generate-book-cover`, { method: 'POST' });
        if (coverRes.ok) {
          const coverData = await coverRes.json();
          coverUrl = coverData.cover_url;
        }
      } catch {
        // Cover is optional — proceed without it
      }

      // Get user name for the title
      let userName = 'My';
      try {
        const meRes = await fetchWithAuth(`${API_BASE}/api/auth/me`);
        if (meRes.ok) {
          const meData = await meRes.json();
          userName = meData.first_name || meData.username || 'My';
        }
      } catch { /* fallback */ }

      // Build HTML and save to S3 + print
      const bookHtml = buildRecipeBookHTML(recipesWithImages, userName, coverUrl);
      if (!bookHtml) throw new Error('Failed to build recipe book');

      // Save to backend
      try {
        await fetchWithAuth(`${API_BASE}/api/recipes/books`, {
          method: 'POST',
          body: JSON.stringify({
            html_content: bookHtml,
            title: `${userName}'s Recipe Book`,
            recipe_ids: recipesWithImages.map(r => r.id),
            cover_url: coverUrl
          })
        });
      } catch { /* save failure is non-fatal — still print */ }

      printHTMLDocument(bookHtml, `${userName}'s Recipe Book`);
    } catch (e) {
      alert('Failed to generate Recipe Book: ' + e.message);
    } finally {
      setRecipeBookGenerating(false);
    }
  };

  // Click outside handler for overflow menu
  useEffect(() => {
    if (!openOverflowId) return;
    const handler = (e) => {
      if (!e.target.closest('.rcl-overflowWrapper')) {
        setOpenOverflowId(null);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openOverflowId]);

  // Add to Cart handler — appends to active shopping list
  const handleAddToCart = async () => {
    setAddToCartLoading(true);
    try {
      const body = {
        recipe_id: addToCartRecipeId,
        servings_multiplier: addToCartServings
      };
      const response = await fetchWithAuth(`${API_BASE}/api/shopping-lists/generate-from-recipe`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const result = await response.json();
        const itemCount = result.items_added || 0;
        alert(`Added ${itemCount} items to your shopping list`);
        loadActiveList();
        loadNutritionData();
        setShowAddToCartModal(false);
        setAddToCartRecipeId(null);
        setAddToCartRecipeName('');
        setAddToCartServings(1);
      } else if (response.status === 409) {
        const errData = await response.json().catch(() => ({}));
        if (errData.detail === 'CART_FULFILLED') {
          setShowAddToCartModal(false);
          handleCartFulfilledError(() => handleAddToCart());
          return;
        }
      } else {
        const errData = await response.json().catch(() => ({}));
        const detail = errData.detail || 'Failed to generate shopping list';
        alert(typeof detail === 'string' ? detail : detail.map(d => d.msg || d).join(', '));
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding to cart: ' + error.message);
    } finally {
      setAddToCartLoading(false);
    }
  };

  // Check Pantry handler — verifies shopping list items against user's pantries
  const handleVerifyAgainstPantry = async () => {
    if (!selectedShoppingList) return;
    try {
      // Get user's pantries
      const pantryRes = await fetchWithAuth(`${API_BASE}/api/pantry/user/list-all`);
      if (!pantryRes.ok) {
        alert('Failed to load pantries');
        return;
      }
      const pantryData = await pantryRes.json();
      const pantryIds = pantryData.pantries?.map(p => p.id) || [];
      if (pantryIds.length === 0) {
        alert('No pantries found. Create a pantry first.');
        return;
      }
      // Verify against pantry
      const verifyRes = await fetchWithAuth(
        `${API_BASE}/api/shopping-lists/${selectedShoppingList.id}/verify-against-pantry`,
        {
          method: 'POST',
          body: JSON.stringify({ pantry_ids: pantryIds })
        }
      );
      if (verifyRes.ok) {
        const result = await verifyRes.json();
        setVerificationResult(result);
      } else {
        const errData = await verifyRes.json().catch(() => ({}));
        alert(errData.detail || 'Verification failed');
      }
    } catch (error) {
      console.error('Error checking pantry:', error);
      alert('Error checking pantry: ' + error.message);
    }
  };

  if (selectedPantry) {
    return (
      <PantryDetailView
        pantry={selectedPantry}
        onBack={() => {
          setSelectedPantry(null);
          loadNutritionData();
        }}
        fetchWithAuth={fetchWithAuth}
      />
    );
  }

  return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Nutrition Management</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>← Dashboard</button>
        </div>

        <div className="page-nav-bar">
          {/* ===== PILL TOOLBAR — Quick Action Tools ===== */}
          <div className="pill-toolbar">
            <button className="pill-btn" onClick={() => setShowFamilyGuard(true)}>
              Family Allergen Guard
            </button>
            <button className="pill-btn" onClick={() => setShowCarbonDashboard(true)}>
              Carbon Footprint
            </button>
            <button className="pill-btn" onClick={() => setShowNutritionComparison(true)}>
              Compare Products
            </button>
            <button className="pill-btn" onClick={() => setShowGroceryCapture(true)}>
              Scan Groceries
            </button>
            <button className="pill-btn" onClick={() => setShowDietCheck(true)}>
              Diet Check
            </button>
            <button className="pill-btn" onClick={() => setShowWhatCanICook(true)}>
              What Can I Cook?
            </button>
          </div>

          {/* ===== 6-TAB BAR ===== */}
          <div className="tab-bar">
        <button
          className={`tab-item${nutritionSubTab === 'preferences' ? ' tab-item--active' : ''}`}
          onClick={() => setNutritionSubTab('preferences')}
        >
          Food Preferences
        </button>
        <button
          className={`tab-item${nutritionSubTab === 'dietplans' ? ' tab-item--active' : ''}`}
          onClick={() => setNutritionSubTab('dietplans')}
        >
          Diet Plans
        </button>
        <button
          className={`tab-item${nutritionSubTab === 'mealplans' ? ' tab-item--active' : ''}`}
          onClick={() => setNutritionSubTab('mealplans')}
        >
          Meal Plans
        </button>
        <button
          className={`tab-item${nutritionSubTab === 'pantry' ? ' tab-item--active' : ''}`}
          onClick={() => setNutritionSubTab('pantry')}
        >
          My Pantry
        </button>
        <button
          className={`tab-item${nutritionSubTab === 'recipes' ? ' tab-item--active' : ''}`}
          onClick={() => setNutritionSubTab('recipes')}
        >
          Recipes
        </button>
        <button
          className={`tab-item${nutritionSubTab === 'shopping' ? ' tab-item--active' : ''}`}
          onClick={() => setNutritionSubTab('shopping')}
        >
          Shopping Carts
        </button>
      </div>
        </div>
      </div>

      <div className="page-scroll-content">
      {/* ===== TAB 1: Food Preferences ===== */}
      {nutritionSubTab === 'preferences' && (
        <div className="card mb-4">
          <div className="card-header">
            <h3 className="card-title">Food Preferences</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowPreferenceForm(true)}
            >
              Add Preference
            </button>
          </div>

          {preferences.length === 0 ? (
            <p>No food preferences set. Add your dietary needs and preferences!</p>
          ) : (
            <div>
              {preferences.map(pref => (
                <div key={pref.id} className="preference-item">
                  <div className="preference-content">
                    <span>{pref.name}</span>
                    <span className="preference-meta">
                      {pref.preference_type} (Rank: {pref.preference_ranking})
                    </span>
                  </div>
                  <div className="preference-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleEditPreference(pref)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeletePreference(pref.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== TAB 2: Diet Plans ===== */}
      {nutritionSubTab === 'dietplans' && (
        <DietPlanManagement fetchWithAuth={fetchWithAuth} />
      )}

      {/* ===== TAB 3: Meal Plans ===== */}
      {nutritionSubTab === 'mealplans' && (
        <MealPlanManagement
          fetchWithAuth={fetchWithAuth}
          fetchWithAuthFormData={fetchWithAuthFormData}
          refreshKey={refreshKey}
          onDataChanged={loadNutritionData}
          onOpenAIAgent={(tool) => {
            try {
              setAiAgentInitialTool(tool);
              setShowAIAssistant(true);
            } catch (error) {
              alert('Error: ' + error.message);
            }
          }}
        />
      )}

      {/* ===== TAB 4: My Pantry ===== */}
      {nutritionSubTab === 'pantry' && (
        <div className="card mb-4">
          <div className="card-header">
            <h3 className="card-title">My Pantry</h3>
          </div>
          {pantry ? (
            <div className="preference-item">
              <div className="preference-content">
                <span>{pantry.name}</span>
                <span className="preference-meta">
                  {pantry.items?.length || 0} items
                </span>
              </div>
              <div className="preference-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setSelectedPantry(pantry)}
                >
                  View Pantry
                </button>
              </div>
            </div>
          ) : (
            <p>Loading pantry...</p>
          )}
        </div>
      )}

      {/* ===== TAB 5: Recipes ===== */}
      {nutritionSubTab === 'recipes' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recipe Management</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowRecipeForm(true)}
            >
              Add Recipe
            </button>
          </div>

          {/* Filter toggle: All Recipes / My Recipe Book */}
          <div className="rcl-filterRow">
            <div className="rcl-filterToggle">
              <button
                className={recipeBookFilter === 'all' ? 'active' : ''}
                onClick={() => { setRecipeBookFilter('all'); setRecipePage(1); setSelectedRecipes(new Set()); }}
              >
                All Recipes
              </button>
              <button
                className={recipeBookFilter === 'favorites' ? 'active' : ''}
                onClick={() => { setRecipeBookFilter('favorites'); setRecipePage(1); setSelectedRecipes(new Set()); }}
              >
                My Recipe Book {favoritesCount > 0 && <span className="rcl-bookCount">{favoritesCount}</span>}
              </button>
            </div>
            {recipeBookFilter === 'all' && (
              <span className="rcl-filterHint">
                Click <span className="rcl-goldHeart">{'\u2665'}</span> on any recipe to add it to My Recipe Book
              </span>
            )}
          </div>

          {/* My Recipe Book bar (favorites view only) */}
          {recipeBookFilter === 'favorites' && favoritesCount > 0 && (
            <div className="rcl-bookBar">
              <span className="rcl-bookIcon">{'\ud83d\udcd6'}</span>
              <span className="rcl-bookLabel">My Recipe Book</span>
              <span className="rcl-bookCountLabel">{favoritesCount} recipe{favoritesCount !== 1 ? 's' : ''}</span>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleGenerateRecipeBook}
                disabled={recipeBookGenerating}
              >
                {recipeBookGenerating ? <><span className="rcl-spinner"></span>Generating...</> : 'Create PDF Recipe Book'}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setShowRecipeBookModal(true); loadRecipeBooks(); }}
              >
                View Recipe Books
              </button>
            </div>
          )}

          <ListControls
            searchValue={recipeSearch}
            onSearchChange={(val) => { setRecipeSearch(val); setRecipePage(1); }}
            searchPlaceholder="Search recipes..."
            sortValue={recipeSort}
            onSortChange={(val) => { setRecipeSort(val); setRecipePage(1); }}
            sortOptions={[
              { value: 'newest', label: 'Most Recent' },
              { value: 'oldest', label: 'Oldest' },
              { value: 'alpha', label: 'Alphabetical (A-Z)' }
            ]}
            perPage={recipePerPage}
            onPerPageChange={(val) => { setRecipePerPage(val); setRecipePage(1); }}
            totalItems={recipesTotal}
          />

          {/* Bulk action bar — navy gradient, always visible */}
          <div className="rcl-bulkBar">
            <span className="rcl-bulkCount">
              {selectedRecipes.size > 0 ? `${selectedRecipes.size} selected` : 'None selected'}
            </span>
            <button
              className="btn btn-bulk-delete"
              disabled={selectedRecipes.size === 0}
              onClick={handleBulkDelete}
            >
              Delete Selected
            </button>
            <button
              className="btn"
              disabled={selectedRecipes.size === 0}
              onClick={handleBulkFavorite}
            >
              Add to Recipe Book
            </button>
            <button
              className="btn"
              disabled={selectedRecipes.size === 0}
              onClick={handlePrintSelected}
            >
              Print Selected
            </button>
            <button
              className="btn btn-deselect"
              disabled={selectedRecipes.size === 0}
              onClick={handleDeselectAll}
            >
              Deselect All
            </button>
          </div>

          {recipesLoading ? (
            <p>Loading recipes...</p>
          ) : recipes.length === 0 ? (
            <p>{recipeSearch ? 'No recipes match your search.' : (recipeBookFilter === 'favorites' ? 'No recipes in your Recipe Book yet. Click \u2665 on any recipe to add it!' : 'No recipes added yet. Add your first recipe!')}</p>
          ) : (
            <div>
              {/* Select All row */}
              <div className="rcl-selectAllRow">
                <input
                  type="checkbox"
                  className="rcl-checkbox"
                  checked={recipes.length > 0 && selectedRecipes.size === recipes.length}
                  onChange={handleSelectAllRecipes}
                />
                <label onClick={handleSelectAllRecipes}>
                  Select All ({recipes.length} recipe{recipes.length !== 1 ? 's' : ''})
                </label>
              </div>

              {recipes.map(recipe => (
                <div key={recipe.id} className={`rcl-card${selectedRecipes.has(recipe.id) ? ' rcl-card--selected' : ''}`}>
                  <div className="rcl-titleRow">
                    <input
                      type="checkbox"
                      checked={selectedRecipes.has(recipe.id)}
                      onChange={() => handleSelectRecipe(recipe.id)}
                    />
                    <button
                      className={`rcl-heartBtn ${recipe.is_favorite ? 'rcl-heartBtn--active' : 'rcl-heartBtn--inactive'}`}
                      onClick={() => handleToggleFavorite(recipe.id)}
                      title={recipe.is_favorite ? 'Remove from Recipe Book' : 'Add to Recipe Book'}
                    >
                      {recipe.is_favorite ? '\u2665' : '\u2661'}
                    </button>
                    <div className="rcl-titleContent">
                      <h4>{recipe.title}</h4>
                      {recipe.description && <p>{recipe.description}</p>}
                      <div className="recipe-meta">
                        {recipe.recipe_date && (
                          <span>Date: {formatDateOnly(recipe.recipe_date)}</span>
                        )}
                        {recipe.recipe_status && (
                          <span> {'\u2022'} Status: {recipe.recipe_status.replace('_', ' ')}</span>
                        )}
                        {recipe.recipe_source && (
                          <span> {'\u2022'} Source: {recipe.recipe_source.replace('_', ' ')}</span>
                        )}
                        {recipe.include_in_meal_plan && (
                          <span className="rcl-mealPlanBadge">
                            {'\u2713'} In Meal Plans
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rcl-primaryActions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => openRecipeViewer(recipe)}
                    >
                      View Recipe
                    </button>
                    <button
                      className="btn btn-sm shl-addCartBtn"
                      onClick={() => {
                        setAddToCartRecipeId(recipe.id);
                        setAddToCartRecipeName(recipe.title);
                        setShowAddToCartModal(true);
                      }}
                    >
                      Add to Cart
                    </button>
                    <button
                      className="btn btn-sm rcl-cookBtn"
                      onClick={() => handleMakeRecipe(recipe.id, recipe.title)}
                    >
                      Cook This
                    </button>

                    <div className="rcl-overflowWrapper">
                      <button
                        className="rcl-overflowBtn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenOverflowId(openOverflowId === recipe.id ? null : recipe.id);
                        }}
                      >
                        Other Actions...
                      </button>
                      {openOverflowId === recipe.id && (
                        <div className="rcl-overflowMenu">
                          <button onClick={() => { handlePrintSingleCard(recipe); setOpenOverflowId(null); }}>
                            Print Card
                          </button>
                          <button onClick={() => { handleEditRecipe(recipe); setOpenOverflowId(null); }}>
                            Edit
                          </button>
                          <button className="rcl-overflowDanger" onClick={() => { handleDeleteRecipe(recipe.id); setOpenOverflowId(null); }}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {recipePerPage > 0 && (
            <Pagination
              currentPage={recipePage}
              totalPages={recipePerPage > 0 ? Math.ceil(recipesTotal / recipePerPage) : 1}
              onPageChange={setRecipePage}
            />
          )}
        </div>
      )}

      {/* ===== TAB 6: Shopping Cart (Single Card — Pending or Fulfilled) ===== */}
      {nutritionSubTab === 'shopping' && (
        <div className="card mt-24">
          <div className="card-header">
            <h3 className="card-title">Shopping Cart</h3>
          </div>

          {activeShoppingList ? (
            <div className="sla-cartCard">
              <div className="sla-cartStatus">
                <span className={`sla-cartBadge${activeShoppingList.status === 'fulfilled' ? ' sla-cartBadge--fulfilled' : ' sla-cartBadge--pending'}`}>
                  {activeShoppingList.status === 'fulfilled' ? 'Fulfilled' : 'Pending'}
                </span>
                <span className="sla-cartCount">
                  {activeShoppingList.instacart_link_url && activeShoppingList.status === 'pending' && activeShoppingList.verification_results?.instacart_selected_indices
                    ? `${activeShoppingList.verification_results.instacart_selected_indices.length} of ${activeShoppingList.items?.length || 0} items ordered`
                    : `${activeShoppingList.items?.length || 0} items`}
                </span>
              </div>

              <p className="sla-cartMessage">
                {activeShoppingList.status === 'fulfilled'
                  ? 'All items have been shopped. Reprint to re-shop the same list, or Delete to start fresh.'
                  : activeShoppingList.instacart_link_url && activeShoppingList.status === 'pending'
                    ? 'Items were sent to Instacart. Confirm your purchase when done shopping.'
                    : activeShoppingList.items?.length > 0
                      ? 'Your shopping list is ready. Add more items, print, or send to Instacart.'
                      : 'Your shopping list is empty. Add items from recipes using "Add to Cart" or add manually.'}
              </p>

              <div className="sla-cartActions">
                {activeShoppingList.status === 'fulfilled' ? (
                  <>
                    {activeShoppingList.items?.length > 0 && (
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowCartViewModal(true)}>
                        View
                      </button>
                    )}
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => printShoppingList(activeShoppingList)}
                    >
                      Reprint
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={async () => {
                        if (!(await confirm({
                          title: 'Start a fresh shopping list?',
                          message: 'This fulfilled list will be deleted and a new empty list started in its place.',
                          confirmLabel: 'Delete and Start Fresh',
                          destructive: true,
                        }))) return;
                        try {
                          await fetchWithAuth(`${API_BASE}/api/shopping-lists/${activeShoppingList.id}`, {
                            method: 'DELETE'
                          });
                          loadActiveList();
                        } catch (err) {
                          console.error('Failed to delete list:', err);
                          alert('Failed to delete list');
                        }
                      }}
                    >
                      Delete
                    </button>
                  </>
                ) : activeShoppingList.instacart_link_url && activeShoppingList.status === 'pending' ? (
                  /* Instacart-pending: items sent but not yet confirmed */
                  <>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowCartViewModal(true)}>
                      View
                    </button>
                    <button
                      className="btn btn-sm shl-instacartBtn"
                      onClick={() => handleSendToInstacart(activeShoppingList)}
                    >
                      <InstacartCarrotLogo size={16} />
                      Confirm Order
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={async () => {
                        if (!(await confirm({
                          title: 'Cancel this Instacart order?',
                          message: 'No items will be added to your pantry. Your shopping list is kept.',
                          confirmLabel: 'Cancel Order',
                          cancelLabel: 'Keep Order',
                          destructive: true,
                        }))) return;
                        try {
                          await fetchWithAuth(`${API_BASE}/api/shopping-lists/${activeShoppingList.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ status: 'pending' })
                          });
                          loadActiveList();
                        } catch (err) {
                          console.error('Cancel order error:', err);
                        }
                      }}
                    >
                      Cancel Order
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setEditingShoppingList(activeShoppingList);
                        setShowShoppingListForm(true);
                      }}
                    >
                      Add Items
                    </button>
                    {activeShoppingList.items?.length > 0 && (
                      <>
                        <button className="btn btn-secondary btn-sm" onClick={() => setShowCartViewModal(true)}>
                          View
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handlePrintShoppingList(activeShoppingList)}
                        >
                          Print
                        </button>
                        <button
                          className="btn btn-sm shl-instacartBtn"
                          onClick={() => handleSendToInstacart(activeShoppingList)}
                        >
                          <InstacartCarrotLogo size={16} />
                          Instacart
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowMealogic(true)}
                          title="Order pre-prepared meals matched to your health profile"
                        >
                          Pre-Prepared Meals
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={async () => {
                            if (!(await confirm({
                              title: 'Mark all items as fulfilled?',
                              message: 'Every item on this list will be marked fulfilled and added to your pantry.',
                              confirmLabel: 'Mark All Fulfilled',
                            }))) return;
                            try {
                              const updatedItems = activeShoppingList.items.map(item => ({
                                ...item, checked: true, fulfilled: true
                              }));
                              await fetchWithAuth(`${API_BASE}/api/shopping-lists/${activeShoppingList.id}`, {
                                method: 'PUT',
                                body: JSON.stringify({ items: updatedItems, status: 'fulfilled' })
                              });
                              loadActiveList();
                            } catch (err) {
                              console.error('Failed to fulfill list:', err);
                              alert('Failed to mark list as fulfilled');
                            }
                          }}
                        >
                          Mark Fulfilled
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <p className="sla-emptyState">Your shopping list is empty. Add items from recipes using "Add to Cart" or add manually.</p>
          )}
        </div>
      )}

      {/* Shopping Cart View Modal */}
      {showCartViewModal && activeShoppingList && (
        <div className="modal-overlay" onClick={() => setShowCartViewModal(false)}>
          <div className="modal-content modal-content-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Shopping Cart</h2>
              <button className="modal-close" onClick={() => setShowCartViewModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="sla-cartStatus" style={{ marginBottom: 'var(--space-4)' }}>
                <span className={`sla-cartBadge${activeShoppingList.status === 'fulfilled' ? ' sla-cartBadge--fulfilled' : ' sla-cartBadge--pending'}`}>
                  {activeShoppingList.status === 'fulfilled' ? 'Fulfilled' : 'Pending'}
                </span>
                <span className="sla-cartCount">{activeShoppingList.items?.length || 0} items</span>
              </div>
              {activeShoppingList.items?.map((item, idx) => (
                <React.Fragment key={idx}>
                  <div className="sla-viewItem">
                    <span className="sla-viewItemName">{item.name}</span>
                    <span className="sla-viewItemQty">
                      {item.quantity && item.quantity !== 1 ? `${item.quantity} ` : ''}{item.unit || ''}
                    </span>
                  </div>
                  <UhcCompatBadge compatibility={item.uhc_compatibility} />
                </React.Fragment>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary btn-sm" onClick={() => {
                printShoppingList(activeShoppingList);
              }}>Print</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCartViewModal(false)}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {/* Fulfilled Cart Prompt Modal */}
      {showFulfilledCartPrompt && (
        <div className="modal-overlay" onClick={() => setShowFulfilledCartPrompt(false)}>
          <div className="modal-content modal-content-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Shopping Cart Fulfilled</h2>
              <button className="modal-close" onClick={() => { setShowFulfilledCartPrompt(false); setFulfilledCartRetryAction(null); }}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Your current shopping cart has been fulfilled and cannot be modified.</p>
              <p>Would you like to <strong>create a new shopping cart</strong> for these and other new items?</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary btn-sm"
                onClick={handleReplaceFulfilledCart}
              >
                Yes, Create New Cart
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setShowFulfilledCartPrompt(false);
                  setFulfilledCartRetryAction(null);
                  alert('Go to Nutrition \u2192 Shopping Carts to review, print, or delete your fulfilled cart, then come back to add items.');
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showPreferenceForm && (
        <PreferenceForm
          onClose={closePreferenceForm}
          onSave={loadNutritionData}
          fetchWithAuth={fetchWithAuth}
          editingPreference={editingPreference}
        />
      )}

      {showRecipeForm && (
        <RecipeForm
          onClose={closeRecipeForm}
          onSave={loadNutritionData}
          fetchWithAuth={fetchWithAuth}
          editingRecipe={editingRecipe}
          onOpenRecipeGenerator={() => {
            closeRecipeForm();
            setAiAgentInitialTool('recipe');
            setShowAIAssistant(true);
          }}
        />
      )}
      {/* Shopping List Form Modal */}
      {showShoppingListForm && (
        <ShoppingListForm
          onClose={closeShoppingListForm}
          onSave={() => { loadNutritionData(); loadActiveList(); }}
          fetchWithAuth={fetchWithAuth}
          editingList={editingShoppingList}
          onOpenScan={handleOpenCartScan}
          onPrint={handlePrintShoppingList}
        />
      )}

      {/* Shopping List Viewer Modal */}
      {showShoppingListViewer && selectedShoppingList && (
        <div className="slv-overlay">
          <div className="slv-modal">
            {/* Header */}
            <div className="slv-header">
              <h3 className="slv-title">
                🛒 {selectedShoppingList.name}
              </h3>
              <button
                onClick={() => {
                  setShowShoppingListViewer(false);
                  setSelectedShoppingList(null);
                  setVerificationResult(null);
                  setSlvDrugInteractions({});
                }}
                className="slv-closeBtn"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="slv-content">
              {/* Verification Summary Bar */}
              {verificationResult && (
                <div className="slv-verifySummary">
                  You have {verificationResult.already_have_count} of {verificationResult.already_have_count + verificationResult.need_to_buy_count} items in your pantry
                </div>
              )}

              {slvDrugCheckFailed && (
                <UhcCompatBadge compatibility={{ severity: 'unavailable' }} />
              )}
              {selectedShoppingList.items && selectedShoppingList.items.length > 0 ? (
                <div>
                  {selectedShoppingList.items.map((item, idx) => {
                    // Handle both manual format (name, quantity number, unit) and AI format (name, quantity string)
                    const itemName = item.name || item.item_name;
                    // Format quantity with rounding and include unit
                    const formatQty = (qty) => typeof qty === 'number' ? parseFloat(qty.toFixed(2)) : qty;
                    const quantityDisplay = item.quantity != null
                      ? `${formatQty(item.quantity)}${item.unit ? ' ' + item.unit : ''}`
                      : null;

                    // Find verification badge for this item
                    const verifyHave = verificationResult?.already_have?.find(
                      v => v.name?.toLowerCase() === itemName?.toLowerCase()
                    );
                    const verifyNeed = verificationResult?.need_to_buy?.find(
                      v => v.name?.toLowerCase() === itemName?.toLowerCase()
                    );

                    return (
                      <div key={idx} className="slv-itemRow">
                        <div className="flex-1">
                          <span className="font-medium">{itemName}</span>
                          {quantityDisplay && (
                            <span className="slv-itemQty">
                              ({quantityDisplay})
                            </span>
                          )}
                          {item.category && (
                            <span className="slv-itemCategory">
                              {item.category}
                            </span>
                          )}
                          {item.times_needed && item.times_needed > 1 && (
                            <span className="slv-itemMealCount">
                              ×{item.times_needed} meals
                            </span>
                          )}
                          {verifyHave && (
                            <span className="slv-verifyBadge slv-verifyBadge--have">
                              ✓ In Pantry ({formatQty(verifyHave.available)} {item.unit || ''})
                            </span>
                          )}
                          {verifyNeed && (
                            <span className="slv-verifyBadge slv-verifyBadge--need">
                              Need ({formatQty(verifyNeed.shortfall)} {item.unit || ''})
                            </span>
                          )}
                          {(() => {
                            // Apple 1.4.1 (G5, 2026-10-17): the badge is a BUTTON. Its explanation
                            // used to live only in a hover tooltip, which cannot be opened on an
                            // iPad (the review device). Tapping now shows the explanation and its
                            // citations inline; the tooltip stays for desktop.
                            const drugIx = slvDrugInteractions[itemName] || [];
                            return drugIx.map((ix, dIdx) => {
                              const k = `${idx}-${dIdx}`;
                              const open = openDrugBadge === k;
                              return (
                                <React.Fragment key={dIdx}>
                                  <button
                                    type="button"
                                    className={`slv-drugBadge slv-drugBadge--${ix.severity}`}
                                    title={`${ix.mechanism} — ${ix.guidance}`}
                                    aria-expanded={open}
                                    onClick={(e) => { e.stopPropagation(); setOpenDrugBadge(open ? null : k); }}
                                  >
                                    {ix.severity === 'critical' ? '⚠️' : '⚡'} {ix.medication}
                                  </button>
                                  {open && (
                                    <div className="slv-drugDetail" onClick={(e) => e.stopPropagation()}>
                                      <div><strong>{itemName} + {ix.medication}:</strong> {ix.mechanism}</div>
                                      <div>{ix.guidance}</div>
                                      <CitationChip sources={ix.sources} featureName="Food-Drug Interaction Check"
                                                    itemTitle={`${itemName} + ${ix.medication}`} />
                                    </div>
                                  )}
                                </React.Fragment>
                              );
                            });
                          })()}
                        </div>
                        {item.checked && (
                          <span className="text-green">✓</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="slv-emptyText">No items in this list</p>
              )}

              {/* Summary */}
              <div className="slv-summary">
                <div className="slv-summaryRow">
                  <span>Total Items:</span>
                  <strong>{selectedShoppingList.items?.length || 0}</strong>
                </div>
                {selectedShoppingList.estimated_cost && (
                  <div className="slv-summaryRow">
                    <span>Final Cost:</span>
                    <strong>${selectedShoppingList.estimated_cost.toFixed(2)}</strong>
                  </div>
                )}
                {selectedShoppingList.store_preference && (
                  <div className="flex-between">
                    <span>Store:</span>
                    <strong>{selectedShoppingList.store_preference}</strong>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="slv-footer">
              <button
                onClick={() => {
                  setShowShoppingListViewer(false);
                  setSelectedShoppingList(null);
                  setVerificationResult(null);
                  setSlvDrugInteractions({});
                }}
                className="slv-closeFooterBtn"
              >
                Close
              </button>

              {/* Check Pantry Button */}
              <button
                onClick={handleVerifyAgainstPantry}
                className="slv-checkPantryBtn"
              >
                Check Pantry
              </button>

              {/* Print Button */}
              <button
                onClick={() => {
                  printShoppingList(selectedShoppingList);
                  if (selectedShoppingList?.id) {
                    fetchWithAuth(`${API_BASE}/api/shopping-lists/${selectedShoppingList.id}/status?new_status=printed`, { method: 'PATCH' })
                      .then(() => loadNutritionData())
                      .catch(err => console.error('Failed to update print status:', err));
                  }
                }}
                className="slv-printBtn"
              >
                Print
              </button>

              {/* Fulfill Button - Only show if not already fulfilled */}
              {selectedShoppingList.status !== 'fulfilled' && (
                <button
                  onClick={() => setShowFulfillModal(true)}
                  className="slv-fulfillBtn"
                >
                  ✓ Mark as Received
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Instacart Order Review Modal — 3-stage */}
      {instacartReview.show && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-lg">
            <div className="modal-header">
              <h2>
                {instacartReview.stage === 1 ? 'Review Order for Instacart'
                  : instacartReview.stage === 2 ? 'Directed to Instacart'
                  : 'Confirm Instacart Order'}
              </h2>
              <button className="modal-close" onClick={handleIcrClose}>✕</button>
            </div>
            <div className="modal-body">

              {/* ===== STAGE 1: Order Review ===== */}
              {instacartReview.stage === 1 && (
                <>
                  {/* Select All bar */}
                  <div className="icr-selectAll">
                    <label className="icr-checkLabel">
                      <input
                        type="checkbox"
                        checked={instacartReview.selectedIndices.length === (instacartReview.list?.items?.length || 0)}
                        onChange={handleIcrSelectAll}
                      />
                      Select All
                    </label>
                    <span className="icr-selectedCount">
                      {instacartReview.selectedIndices.length} of {instacartReview.list?.items?.length || 0} selected
                    </span>
                  </div>

                  {/* Scrollable item list */}
                  <div className="icr-itemList">
                    {(instacartReview.list?.items || []).map((item, idx) => (
                      <div key={idx} className={`icr-itemRow${instacartReview.selectedIndices.includes(idx) ? ' icr-itemRow--selected' : ''}`}>
                        <label className="icr-checkLabel">
                          <input
                            type="checkbox"
                            checked={instacartReview.selectedIndices.includes(idx)}
                            onChange={() => handleIcrToggleItem(idx)}
                          />
                        </label>
                        <span className="icr-itemName">{item.name}</span>
                        <span className="icr-itemQty">
                          {item.quantity || 1} {item.unit || ''}
                        </span>
                        {item.added_via && (
                          <span className="icr-itemBadge">Added</span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Add Item section */}
                  <div className="icr-addItemSection">
                    {!instacartReview.showAddItem ? (
                      <div className="icr-addItemBtns">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setInstacartReview(prev => ({ ...prev, showAddItem: true }))}
                        >
                          + Add Item
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setInstacartReview(prev => ({ ...prev, showBarcodeLookup: true }))}
                        >
                          Scan / Enter UPC
                        </button>
                      </div>
                    ) : (
                      <div className="icr-addItemForm">
                        <input
                          type="text"
                          className="form-input icr-addInput"
                          placeholder="Item name *"
                          value={instacartReview.addItemForm.name}
                          onChange={e => setInstacartReview(prev => ({
                            ...prev, addItemForm: { ...prev.addItemForm, name: e.target.value }
                          }))}
                        />
                        <input
                          type="number"
                          className="form-input icr-addInputSm"
                          placeholder="Qty"
                          min="0.1"
                          step="0.1"
                          value={instacartReview.addItemForm.quantity}
                          onChange={e => setInstacartReview(prev => ({
                            ...prev, addItemForm: { ...prev.addItemForm, quantity: e.target.value }
                          }))}
                        />
                        <input
                          type="text"
                          className="form-input icr-addInputSm"
                          placeholder="Unit"
                          value={instacartReview.addItemForm.unit}
                          onChange={e => setInstacartReview(prev => ({
                            ...prev, addItemForm: { ...prev.addItemForm, unit: e.target.value }
                          }))}
                        />
                        <button className="btn btn-primary btn-sm" onClick={handleIcrAddManualItem}>
                          Add
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setInstacartReview(prev => ({ ...prev, showAddItem: false }))}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Footer actions */}
                  <div className="icr-footer">
                    <button onClick={handleIcrClose} className="btn btn-secondary">
                      Cancel
                    </button>
                    <button
                      onClick={handleIcrSendToInstacart}
                      className="btn icr-instacartCta"
                      disabled={instacartReview.selectedIndices.length === 0}
                    >
                      <InstacartCarrotLogo size={18} />
                      Send to Instacart ({instacartReview.selectedIndices.length} items)
                    </button>
                  </div>

                  <div className="ic-footer">
                    <span className="ic-footerLabel">Powered by</span>
                    <InstacartCarrotLogo size={12} />
                    <span className="ic-footerBrand">Instacart</span>
                  </div>
                  <p className="ic-footerTrademark">
                    INSTACART® is a registered trademark of Maplebear Inc. d/b/a Instacart.
                  </p>
                </>
              )}

              {/* ===== STAGE 2: Redirected to Instacart ===== */}
              {instacartReview.stage === 2 && (
                <>
                  <div className="ic-successCard">
                    <p className="ic-successTitle">Your items have been sent to Instacart</p>
                    <p className="ic-successDesc">
                      Complete your order on Instacart, then return here<br/>
                      to confirm what you purchased.
                    </p>
                  </div>

                  <button onClick={handleIcrClose} className="btn btn-primary ic-ctaBtn">
                    Return to Cart
                  </button>

                  <div className="ic-footer">
                    <span className="ic-footerLabel">Powered by</span>
                    <InstacartCarrotLogo size={12} />
                    <span className="ic-footerBrand">Instacart</span>
                  </div>
                  <p className="ic-footerTrademark">
                    INSTACART® is a registered trademark of Maplebear Inc. d/b/a Instacart.
                  </p>
                </>
              )}

              {/* ===== STAGE 3: Post-Shopping Confirmation ===== */}
              {instacartReview.stage === 3 && (
                <>
                  <p className="icr-confirmDesc">
                    These items were sent to Instacart. Confirm your purchase to add them to your pantry.
                  </p>
                  <div className="icr-itemList">
                    {(instacartReview.list?.items || [])
                      .map((item, idx) => ({ item, idx }))
                      .filter(({ idx }) => instacartReview.selectedIndices.includes(idx))
                      .map(({ item, idx }) => (
                        <div key={idx} className="icr-itemRow icr-itemRow--selected">
                          <span className="icr-itemName">{item.name}</span>
                          <span className="icr-itemQty">
                            {item.quantity || 1} {item.unit || ''}
                          </span>
                        </div>
                      ))}
                  </div>

                  <div className="icr-footer">
                    <button onClick={handleIcrCancelOrder} className="btn btn-secondary">
                      Cancel Order
                    </button>
                    <button onClick={handleIcrMarkFulfilled} className="btn btn-primary">
                      Mark Fulfilled
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Barcode Lookup for Instacart Review — Add Item */}
      {instacartReview.showBarcodeLookup && (
        <BarcodeLookupModal
          onClose={() => setInstacartReview(prev => ({ ...prev, showBarcodeLookup: false }))}
          onProductFound={handleIcrBarcodeProduct}
          fetchWithAuth={fetchWithAuth}
          title="Add Item by Barcode"
        />
      )}

      {/* Print Preview Modal */}
      {/* Fulfill Shopping List Modal */}
      {showFulfillModal && selectedShoppingList && (
        <div className="modal-overlay" style={{zIndex: 1001}}>
          <div className="slf-modal">
            {/* Header */}
            <div className="slf-header">
              <h3 className="slf-title">
                ✓ Fulfill Shopping List
              </h3>
              <p className="slf-description">
                Transfer items to your pantry
              </p>
            </div>

            {/* Content */}
            <div className="slf-content">
              {/* Pantry destination - auto-set */}
              <div className="slf-pantryInfo">
                <p className="m-0">
                  ✓ Items will be added to: <strong>{pantry?.name || 'My Pantry'}</strong>
                </p>
              </div>

              {/* Items to Fulfill */}
              <div>
                <label className="slf-itemsLabel">
                  Items ({selectedShoppingList.items?.length || 0})
                </label>
                <p className="slf-instruction">
                  All items will be added. Uncheck any you didn't purchase.
                </p>

                <div className="slf-itemsList">
                  {selectedShoppingList.items?.map((item, idx) => {
                    const isSelected = fulfillSelectedItems.length === 0 || fulfillSelectedItems.includes(idx);
                    const alreadyFulfilled = item.fulfilled;

                    return (
                      <div
                        key={idx}
                        className="slf-itemRow" style={{ borderBottom: idx < selectedShoppingList.items.length - 1 ? '1px solid var(--brand-border)' : 'none', backgroundColor: alreadyFulfilled ? '#EBF0F7' : 'white', opacity: alreadyFulfilled ? 0.7 : 1 }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected && !alreadyFulfilled}
                          disabled={alreadyFulfilled}
                          onChange={() => toggleFulfillItem(idx)}
                          className="slf-checkbox"
                        />
                        <div className="flex-1">
                          <div className="slf-itemName">
                            {item.name || item.item_name}
                          </div>
                          {(item.quantity || item.unit) && (
                            <div className="slf-itemQty">
                              {typeof item.quantity === 'string'
                                ? item.quantity
                                : `${item.quantity || ''} ${item.unit || ''}`}
                            </div>
                          )}
                        </div>
                        {alreadyFulfilled && (
                          <span className="slf-addedBadge">
                            ✓ Added
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="slf-footer">
              <button
                onClick={() => {
                  setShowFulfillModal(false);
                  setFulfillSelectedItems([]);
                }}
                className="slf-cancelBtn"
              >
                Cancel
              </button>
              <button
                onClick={handleFulfillShoppingList}
                disabled={!pantry || isFulfilling}
                className="slf-confirmBtn" style={{ backgroundColor: pantry ? 'var(--color-success)' : 'var(--brand-text-muted)', cursor: pantry ? 'pointer' : 'not-allowed' }}
              >
                {isFulfilling ? 'Adding to Pantry...' : '✓ Add to Pantry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Viewer Modal */}
      {showRecipeViewer && selectedRecipeForView && (
        <div className="modal-overlay">
          <div className="rv-modal">
            {/* Header */}
            <div className="rv-header">
              <h3 className="rv-title">
                {selectedRecipeForView.title}
              </h3>
              <button
                onClick={closeRecipeViewer}
                className="rv-closeBtn"
              >
                ×
              </button>
            </div>

            {/* Recipe Content - Scrollable */}
            <div className="rv-content">
              {recipeViewUrl ? (
                <iframe
                  src={recipeViewUrl}
                  className="rv-iframe"
                  title={selectedRecipeForView.title}
                  data-testid="recipe-view-iframe"
                />
              ) : (
                <div className="rv-loading" style={{ padding: '32px', textAlign: 'center', color: '#6B7280' }}>
                  Loading recipe…
                </div>
              )}
            </div>

            {/* Footer with Add to Cart + Make Recipe */}
            <div className="rv-footer">
              <button
                onClick={closeRecipeViewer}
                className="rv-closeFooterBtn"
              >
                Close
              </button>
              {selectedRecipeForView.file_path && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleDownloadRecipeFile(selectedRecipeForView)}
                  data-testid="recipe-download-btn"
                >
                  Download File
                </button>
              )}
              <button
                className="btn btn-sm shl-addCartBtn"
                onClick={() => {
                  setAddToCartRecipeId(selectedRecipeForView.id);
                  setAddToCartRecipeName(selectedRecipeForView.title);
                  setShowAddToCartModal(true);
                }}
              >
                Add to Cart
              </button>
              <button
                onClick={() => handleMakeRecipe(selectedRecipeForView.id, selectedRecipeForView.title)}
                className="rv-makeBtn"
              >
                Make Recipe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Cart Modal — Simplified (always appends to active list) */}
      {showAddToCartModal && (
        <div className="atc-overlay">
          <div className="atc-modal">
            <div className="atc-header">
              <h3>Add to Shopping List</h3>
              <button
                onClick={() => {
                  setShowAddToCartModal(false);
                  setAddToCartRecipeId(null);
                  setAddToCartRecipeName('');
                  setAddToCartServings(1);
                }}
                className="rv-closeBtn"
              >
                ×
              </button>
            </div>
            <div className="atc-body">
              <p className="text-helper">Adding ingredients from <strong>{addToCartRecipeName}</strong> to your shopping list.</p>
              <div className="atc-servingsRow">
                <label>Servings multiplier:</label>
                <input
                  type="number"
                  min="0.5"
                  max="10"
                  step="0.5"
                  value={addToCartServings}
                  onChange={(e) => setAddToCartServings(parseFloat(e.target.value) || 1)}
                  className="atc-servingsInput"
                />
              </div>
            </div>
            <div className="atc-footer">
              <button
                onClick={() => {
                  setShowAddToCartModal(false);
                  setAddToCartRecipeId(null);
                  setAddToCartRecipeName('');
                }}
                className="rv-closeFooterBtn"
              >
                Cancel
              </button>
              <button
                onClick={handleAddToCart}
                className="atc-addBtn"
                disabled={addToCartLoading}
              >
                {addToCartLoading ? 'Adding...' : 'Add to Shopping List'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMealogic && (
        <MealogicHandoff
          fetchWithAuth={fetchWithAuth}
          onClose={() => setShowMealogic(false)}
        />
      )}

      {/* ===== ADDITIONAL MODALS ===== */}
      {showFamilyGuard && (
        <FamilyAllergenGuard
          token={token}
          fetchWithAuth={fetchWithAuth}
          onClose={() => setShowFamilyGuard(false)}
        />
      )}
      {showCarbonDashboard && (
        <CarbonFootprintDashboard
          token={token}
          fetchWithAuth={fetchWithAuth}
          onClose={() => setShowCarbonDashboard(false)}
        />
      )}
      {showNutritionComparison && (
        <NutritionComparisonMode
          token={token}
          fetchWithAuth={fetchWithAuth}
          onClose={() => setShowNutritionComparison(false)}
        />
      )}
      {showCartScanModal && scanCartId && (
        <VisionGroceryCapture
          token={token}
          fetchWithAuth={fetchWithAuth}
          defaultDestination="cart"
          defaultCartId={scanCartId}
          hideDestinationSelector={true}
          onItemsConfirmed={(items, destination) => {
            console.log('Items added to cart:', items);
            loadActiveList();
          }}
          onCartFulfilled={() => {
            setShowCartScanModal(false);
            setScanCartId(null);
            handleCartFulfilledError(() => {
              // After replacing fulfilled cart, re-open scan modal
              loadActiveList();
            });
          }}
          onClose={() => {
            setShowCartScanModal(false);
            setScanCartId(null);
          }}
        />
      )}
      {showGroceryCapture && (
        <VisionGroceryCapture
          token={token}
          fetchWithAuth={fetchWithAuth}
          onItemsConfirmed={(items, destination) => {
            console.log('Items added:', items, 'to:', destination);
            loadActiveList();
            loadNutritionData();
          }}
          onCartFulfilled={() => {
            setShowGroceryCapture(false);
            handleCartFulfilledError(() => loadActiveList());
          }}
          onClose={() => setShowGroceryCapture(false)}
        />
      )}
      {/* NCM scanner is now self-contained in NutritionComparisonMode.jsx */}
      {showDietCheck && (
        <div className="modal-overlay" onClick={() => setShowDietCheck(false)}>
          <div onClick={e => e.stopPropagation()}>
            <DietComplianceChecker
              token={token}
              fetchWithAuth={fetchWithAuth}
              onClose={() => setShowDietCheck(false)}
              embedded={false}
            />
          </div>
        </div>
      )}
      {/* Recipe Book Management Modal */}
      {showRecipeBookModal && (
        <div className="modal-overlay" onClick={() => { setShowRecipeBookModal(false); setViewingBookUrl(null); }}>
          <div className="modal-content modal-content-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>My Recipe Books</h2>
              <button className="modal-close" onClick={() => { setShowRecipeBookModal(false); setViewingBookUrl(null); }}>&times;</button>
            </div>
            <div className="modal-body">
              {viewingBookUrl ? (
                <div>
                  <button className="btn btn-secondary btn-sm" style={{ marginBottom: 12 }} onClick={() => setViewingBookUrl(null)}>
                    &larr; Back to List
                  </button>
                  <iframe
                    src={viewingBookUrl}
                    title="Recipe Book Viewer"
                    className="rbm-viewer"
                  />
                </div>
              ) : recipeBooksLoading ? (
                <p className="rbm-empty">Loading...</p>
              ) : recipeBooks.length === 0 ? (
                <p className="rbm-empty">No recipe books saved yet. Create one from the My Recipe Book view.</p>
              ) : (
                <div className="rbm-list">
                  {recipeBooks.map(book => (
                    <div key={book.id} className="rbm-card">
                      <div className="rbm-cardInfo">
                        <strong>{book.title}</strong>
                        <span className="rbm-meta">{book.recipe_count} recipe{book.recipe_count !== 1 ? 's' : ''} &bull; {new Date(book.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="rbm-cardActions">
                        <button className="btn btn-primary btn-sm" onClick={() => handleViewRecipeBook(book.id)}>View</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => handlePrintRecipeBook(book.id)}>Print</button>
                        <button className="btn btn-bulk-delete btn-sm" onClick={() => handleDeleteRecipeBook(book.id)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ===== END ADDITIONAL MODALS ===== */}

      {/* Post-fulfillment recipe print prompt */}
      {showPostFulfillPrint && postFulfillRecipeIds.length > 0 && (
        <div className="rcl-fulfillPrompt" onClick={() => setShowPostFulfillPrint(false)}>
          <div className="rcl-fulfillPromptCard" onClick={e => e.stopPropagation()}>
            <h3>Print Recipe Cards?</h3>
            <p>
              You just fulfilled items from {postFulfillRecipeIds.length} recipe{postFulfillRecipeIds.length !== 1 ? 's' : ''}.
              Would you like to print the recipe card{postFulfillRecipeIds.length !== 1 ? 's' : ''}?
            </p>
            <div className="rcl-fulfillPromptActions">
              <button
                className="btn btn-primary btn-sm"
                onClick={async () => {
                  try {
                    const ids = postFulfillRecipeIds.join(',');
                    const res = await fetchWithAuth(`${API_BASE}/api/recipes/?ids=${ids}&limit=200`);
                    if (res.ok) {
                      const data = await res.json();
                      printRecipeCards(data.recipes || []);
                    }
                  } catch (e) {
                    console.error('Failed to print recipe cards:', e);
                  }
                  setShowPostFulfillPrint(false);
                  setPostFulfillRecipeIds([]);
                }}
              >
                Print Recipe Cards
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setShowPostFulfillPrint(false); setPostFulfillRecipeIds([]); }}
              >
                No Thanks
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}

export default NutritionTab;
